import AddMediaButton from "@/components/addMediaButton"
import FuzzyFinder, { FuzzyFindWrapperProps } from "@/components/fuzzyFinder"
import MediaCard, { MediaCardProps } from "@/components/mediaCard"
import { apiDeleteRequest, apiGetRequest, apiPostRequest, apiPutRequest } from "@/functions/requests"
import { useApp, useMedia, useProblem, useS3Client } from "@/hooks/contextHooks"
import { ListObjectsV2Command } from "@aws-sdk/client-s3"
import { useAuthenticator } from "@aws-amplify/ui-react"
import { Box, Button, Center, Flex, Heading, IconButton, Input, InputGroup, InputLeftElement, InputRightElement, SimpleGrid, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, ToastId, Tooltip, useToast, useColorModeValue, Select, Switch, FormControl, FormLabel, Progress, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Text } from "@chakra-ui/react"
import { faCloudUploadAlt, faList, faTh, faEdit, faSearch, faTags, faCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate } from "react-router-dom"
import { ReactStateType } from "@xnav/types/src/frontend"
import { NewSection } from "@xnav/types/src/common"
import axios from "axios"
import { nanoid } from "nanoid"
import { useEffect, useRef, useState, useMemo } from "react"
import { useDropzone } from "react-dropzone"

export enum MediaType {
	Video = 0,
	Audio = 1,
	Pdf = 2,
	Image = 3
}

export type MediaMeta = {
	displayMode: string;
	id: string,
	iconId: string,
	name: string,
	type: MediaType,
	iconUrl?: string,
	fileUrl?: string
	uploaded: Date,
	size: number,
	references: string[]
	tags?: string[]
}

const renderWrapper = <T,>({
	data,
	RenderComponent,
	renderComponentProps,
}: FuzzyFindWrapperProps<T>) => {
	const { viewMode = "grid" } = renderComponentProps as { viewMode?: "grid" | "list" }

	const filteredTabs = [
		{ label: "All", filter: () => true },
		{ label: "Videos", filter: (d: any) => d.type === MediaType.Video },
		{ label: "Photos", filter: (d: any) => d.type === MediaType.Image },
		{ label: "PDFs", filter: (d: any) => d.type === MediaType.Pdf },
		{ label: "Audio Files", filter: (d: any) => d.type === MediaType.Audio },
	]

	return (
		<Tabs maxH="100%" display="flex" flexDir="column">
			<TabList>
				{filteredTabs.map(({ label }, i) => (
					<Tab key={i} flex={1}>
						{label}
					</Tab>
				))}
			</TabList>
			<TabPanels flex={1} overflowY="auto">
				{filteredTabs.map(({ filter }, i) => {
					const filtered = data.filter(filter)
					return (
						<TabPanel key={i}>
							{viewMode === "grid" ? (
								<SimpleGrid minChildWidth="300px" spacing={5}>
									{filtered.map((d, i) => (
										<RenderComponent key={i} data={d} {...renderComponentProps} />
									))}
								</SimpleGrid>
							) : (
								<Flex flexDir="column" gap={2} w="full">
									{filtered.map((d, i) => {
										return (
											<Box key={i} w="full">
												<RenderComponent data={d} {...renderComponentProps} viewMode="list" />
											</Box>
										)
									})}
								</Flex>
							)}
						</TabPanel>
					)
				})}
			</TabPanels>
		</Tabs>
	)
}

export default function MediaManager() {
	const navigate = useNavigate()
	const toast = useToast()
	const app = useApp()
	const { user } = useAuthenticator()
	const [media, setMedia] = useMedia() as ReactStateType<MediaMeta[]>
	const [, setProblem] = useProblem()
	const toastRef = useRef<ToastId | undefined>(undefined)
	const [uploadingCount, setUploadingCount] = useState(0)
	const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
	const [editQueue, setEditQueue] = useState<File[]>([])
	const [searchQuery, setSearchQuery] = useState("")
	const [sectionMeta, setSectionMeta] = useState<NewSection[] | undefined>(undefined)

	const [selectedIds, setSelectedIds] = useState<string[]>([])
	const [showUnusedOnly, setShowUnusedOnly] = useState<boolean>(false)
	const [sortBy, setSortBy] = useState<"newest" | "size" | "refs" | "name">("newest")
	const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: onTagClose } = useDisclosure()
	const [tagInput, setTagInput] = useState("")

	const s3 = useS3Client()
	const [courseSize, setCourseSize] = useState<number>(0)

	useEffect(() => {
		if (app?.id && s3) {
			let totalSize = 0
			let isTruncated = true
			let continuationToken: string | undefined = undefined

			const fetchSize = async () => {
				try {
					while (isTruncated) {
						const listCommand = new ListObjectsV2Command({
							Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
							Prefix: `${app.id}/`,
							ContinuationToken: continuationToken
						})
						const listResult = await s3.send(listCommand) as any
						if (listResult.Contents) {
							totalSize += listResult.Contents.reduce((acc: number, obj: any) => acc + (obj.Size || 0), 0)
						}
						isTruncated = listResult.IsTruncated ?? false
						continuationToken = listResult.NextContinuationToken
					}
					setCourseSize(totalSize)
				} catch (e) {
					console.error("Error calculating app size in Media Manager:", e)
				}
			}
			fetchSize()
		}
	}, [app, s3, media])

	const processedMedia = useMemo(() => {
		if (!media) return []
		let items = [...media]

		if (showUnusedOnly) {
			items = items.filter(m => !m.references || m.references.length === 0)
		}

		items.sort((a, b) => {
			if (sortBy === "size") {
				return b.size - a.size
			}
			if (sortBy === "refs") {
				return (b.references?.length || 0) - (a.references?.length || 0)
			}
			if (sortBy === "name") {
				return a.name.localeCompare(b.name)
			}
			return new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime()
		})

		return items
	}, [media, showUnusedOnly, sortBy])

	const handleToggleSelect = (id: string) => {
		setSelectedIds(prev =>
			prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
		)
	}

	const handleBulkDelete = async () => {
		const toastRef = toast({
			title: `Deleting ${selectedIds.length} items`,
			description: "Please wait...",
			status: "warning",
			position: "bottom-left",
			isClosable: true,
			duration: null,
		})
		try {
			await Promise.all(selectedIds.map(id => apiDeleteRequest(`/media/${app.id}/${id}`)))
			setMedia(media => media.filter(m => !selectedIds.includes(m.id)))
			setSelectedIds([])
			toast({ title: "Bulk deletion completed", status: "success", duration: 3000 })
		} catch (e) {
			console.error(e)
			toast({ title: "Failed to delete some items", status: "error", duration: 3000 })
		} finally {
			toast.close(toastRef)
		}
	}

	const handleBulkTag = () => {
		const newTags = tagInput.split(',').map(t => t.trim()).filter(Boolean)
		const updatedMediaList = media.map(m => {
			if (selectedIds.includes(m.id)) {
				const currentTags = m.tags || []
				return {
					...m,
					tags: Array.from(new Set([...currentTags, ...newTags]))
				}
			}
			return m
		})
		Promise.all(
			updatedMediaList
				.filter(m => selectedIds.includes(m.id))
				.map(m => apiPutRequest(`/media/${app.id}/${m.id}`, m))
		)
			.then(() => {
				setMedia(updatedMediaList)
				setSelectedIds([])
				setTagInput("")
				onTagClose()
				toast({ title: "Tags updated successfully", status: "success", duration: 3000 })
			})
			.catch((e) => {
				console.error(e)
				toast({ title: "Failed to update tags", status: "error", duration: 3000 })
			})
	}

	useEffect(() => {
		if (app?.id) {
			apiGetRequest<string, NewSection[]>(`/structure/${app.id}`)
				.then((data: NewSection[]) => {
					setSectionMeta(data)
				})
				.catch(console.error)
		}
	}, [app])



	function validateNewMedia(newMedia: MediaMeta): string | undefined {
		if (newMedia.name === "") return "Name cannot be empty"
		if (!media) throw new Error("No media list downloaded")
		for (const m of media) {
			const name = m.name.slice(0, m.name.lastIndexOf("."))
			if (name === newMedia.name) return
		}
		return undefined
	}

	function markAsEdited() {
		const updatedApp = {
			...app,
			edited: new Date().toString()
		}
		apiPutRequest(`/apps/${app.id}`, updatedApp).catch(console.error)
	}

	function postMedia(newMedia: MediaMeta, file: File, icon?: File) {
		setUploadingCount((uploadingCount) => uploadingCount + 1)
		const commands = [
			apiPostRequest(`/media/${app.id}`, newMedia),
			apiPutRequest<undefined, string>(`/mediaFile/${app.id}/${newMedia.id}`, undefined).then((signedUrl) =>
				axios.put(signedUrl, file)
			),
		]
		if (icon) {
			commands.push(
				apiPutRequest<undefined, string>(`/mediaIcon/${app.id}/${newMedia.iconId}`, undefined).then((signedUrl) =>
					axios.put(signedUrl, icon)
				)
			)
		}
		Promise.all(commands).then(() => {
			setUploadingCount((uploadingCount) => uploadingCount - 1)
			setMedia((media) => [...media, newMedia])
			markAsEdited()
		})
	}

	async function deleteMedia(mediaItem: MediaMeta) {
		const toastRef = toast({
			title: `Deleting ${mediaItem.name}`,
			description: "Please wait...",
			status: "warning",
			position: "bottom-left",
			isClosable: true,
			duration: null,
		})
		await apiDeleteRequest<MediaMeta>(`/media/${app.id}/${mediaItem.id}`)
			.then(() => {
				setMedia((media) => media!.filter((m) => m.id !== mediaItem.id))
				markAsEdited()
			})
			.catch(setProblem)
		toast.close(toastRef)
	}

	function putMedia(newMedia: MediaMeta, file: File, icon?: File) {
		setUploadingCount((uploadingCount) => uploadingCount + 1)
		const commands = [
			apiPutRequest(`/media/${app.id}/${newMedia.id}`, newMedia),
			apiPutRequest<undefined, string>(`/mediaFile/${app.id}/${newMedia.id}`, undefined).then((signedUrl) =>
				axios.put(signedUrl, file)
			),
		]
		if (icon) {
			commands.push(
				apiPutRequest<undefined, string>(`/mediaIcon/${app.id}/${newMedia.iconId}`, undefined).then((signedUrl) =>
					axios.put(signedUrl, icon)
				)
			)
		}
		Promise.all(commands).then(() => {
			setUploadingCount((uploadingCount) => uploadingCount - 1)
			setMedia((media) => media.map(m => m.id === newMedia.id ? newMedia : m))
			markAsEdited()
		})
	}

	function editMediaMeta(newMedia: MediaMeta) {
		apiPutRequest(`/media/${app.id}/${newMedia.id}`, newMedia)
			.then(() => {
				setMedia((media) => media.map(m => m.id === newMedia.id ? newMedia : m))
				markAsEdited()
			})
			.catch(setProblem)
	}

	useEffect(() => {
		if (uploadingCount === 0) {
			toast.close(toastRef.current!)
			toastRef.current = undefined
		} else if (toastRef.current) {
			toast.update(toastRef.current, {
				title: `Uploading ${uploadingCount} new files`,
				description: "Please wait...",
				status: "warning",
				position: "bottom-left",
				isClosable: true,
				duration: null,
			})
		} else {
			toastRef.current = toast({
				title: `Uploading ${uploadingCount} new files`,
				description: "Please wait...",
				status: "warning",
				position: "bottom-left",
				isClosable: true,
				duration: null,
			})
		}
	}, [uploadingCount])

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		noClick: true,
		onDrop: (acceptedFiles) => {
			setEditQueue((prev) => [...prev, ...acceptedFiles])
		}
	})

	if (!media)
		return (
			<Center flex={1} w="100%" flexDir="column" gap={5}>
				<Spinner size="xl" />
				<Heading>Fetching media...</Heading>
			</Center>
		)

	const quotaLimit = 5_000_000_000 // 5 GB
	const percentUsed = Math.min((courseSize / quotaLimit) * 100, 100)
	const totalSizeText = (courseSize / (1024 * 1024)).toFixed(2) + " MB"

	const headerControls = (
		<Flex flexDir="column" gap={3} w="100%">
			<Flex gap={3} align="center" w="100%" flexWrap="wrap">
				<InputGroup size="sm" maxW="380px" flex={1}>
					<InputLeftElement pointerEvents="none" color="gray.400">
						<FontAwesomeIcon icon={faSearch} />
					</InputLeftElement>
					<Input
						placeholder="Search media..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						borderRadius="full"
						bg={useColorModeValue('gray.50', 'gray.700')}
						pr="40px"
					/>
					<InputRightElement mr={1}>
						<Tooltip label={viewMode === "grid" ? "Switch to List View" : "Switch to Grid View"}>
							<IconButton
								aria-label="Toggle View"
								icon={<FontAwesomeIcon icon={viewMode === "grid" ? faList : faTh} />}
								onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
								variant="ghost"
								colorScheme="gray"
								size="xs"
								borderRadius="full"
							/>
						</Tooltip>
					</InputRightElement>
				</InputGroup>

				<Select
					size="sm"
					maxW="180px"
					borderRadius="md"
					value={sortBy}
					onChange={(e) => setSortBy(e.target.value as any)}
					bg={useColorModeValue('white', 'gray.700')}
				>
					<option value="newest">Sort: Newest</option>
					<option value="size">Sort: Largest Size</option>
					<option value="refs">Sort: Most References</option>
					<option value="name">Sort: Name (A-Z)</option>
				</Select>

				<FormControl display="flex" alignItems="center" maxW="150px" gap={2}>
					<Switch
						id="unused-filter"
						size="sm"
						isChecked={showUnusedOnly}
						onChange={(e) => setShowUnusedOnly(e.target.checked)}
					/>
					<FormLabel htmlFor="unused-filter" mb="0" fontSize="xs" fontWeight="semibold">
						Unused Only
					</FormLabel>
				</FormControl>

				<Box flex={1} />

				<Button
					size="sm"
					colorScheme="blue"
					variant="solid"
					leftIcon={<FontAwesomeIcon icon={faEdit} />}
					onClick={() => navigate(`/${app.id}/editor`)}
					borderRadius="md"
					shadow="sm"
				>
					Edit Contents
				</Button>

				<AddMediaButton onAdd={postMedia} onValidate={validateNewMedia} editQueue={editQueue} setEditQueue={setEditQueue} />
			</Flex>

			{/* Course Storage Bar */}
			<Flex align="center" gap={3} px={4} py={2} bg={useColorModeValue('gray.50', 'gray.800')} borderRadius="lg" border="1px solid" borderColor={useColorModeValue('gray.100', 'gray.700')}>
				<Text fontSize="xs" fontWeight="semibold" whiteSpace="nowrap">Course Storage:</Text>
				<Progress value={percentUsed} size="xs" colorScheme={percentUsed > 85 ? "red" : "blue"} flex={1} borderRadius="full" />
				<Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{totalSizeText} of 5.0 GB used ({percentUsed.toFixed(1)}%)</Text>
			</Flex>
		</Flex>
	)

	return (
		<Center minH={0} flex={1} w="100%" p={5} flexDir="column" gap={4} {...getRootProps()} position="relative">
			<input {...getInputProps()} />
			{isDragActive && (
				<Center
					position="absolute"
					inset={0}
					bg="blue.50"
					zIndex={10}
					border="2px dashed"
					borderColor="blue.400"
					borderRadius="md"
					flexDir="column"
					gap={2}
				>
					<FontAwesomeIcon icon={faCloudUploadAlt} size="3x" color="#4299E1" />
					<Heading size="md" color="blue.500">Drop files here to upload</Heading>
				</Center>
			)}

			{selectedIds.length > 0 && (
				<Flex align="center" gap={3} w="full" bg={useColorModeValue('blue.50', 'blue.900/40')} p={2} px={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200" mb={1} shadow="sm">
					<Text fontSize="sm" fontWeight="semibold" color={useColorModeValue('blue.700', 'blue.300')}>{selectedIds.length} items selected</Text>
					<Box flex={1} />
					<Button size="xs" colorScheme="blue" variant="outline" leftIcon={<FontAwesomeIcon icon={faTags} />} onClick={onTagOpen}>
						Tag Selected
					</Button>
					<Button size="xs" colorScheme="red" leftIcon={<FontAwesomeIcon icon={faCheck} />} onClick={handleBulkDelete}>
						Delete Selected
					</Button>
				</Flex>
			)}

			<FuzzyFinder
				data={processedMedia}
				searchKeys={["name", "type"]}
				query={searchQuery}
				RenderComponent={MediaCard}
				RenderWrapper={renderWrapper}
				headerChildren={headerControls}
				renderComponentProps={{
					onDelete: deleteMedia,
					onEdit: editMediaMeta,
					onReplace: putMedia,
					viewMode,
					sectionMeta,
					selectedIds,
					onToggleSelect: handleToggleSelect,
				} as unknown as MediaCardProps} children={undefined} />

			{/* Bulk Tag Modal */}
			<Modal isOpen={isTagOpen} onClose={onTagClose} isCentered size="sm">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Add Tags to Selected</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl>
							<FormLabel fontSize="sm">Tags (comma separated)</FormLabel>
							<Input
								placeholder="e.g. Anatomy, Artifact, Knee"
								value={tagInput}
								onChange={(e) => setTagInput(e.target.value)}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter gap={2}>
						<Button size="sm" variant="ghost" onClick={onTagClose}>Cancel</Button>
						<Button size="sm" colorScheme="blue" onClick={handleBulkTag}>Apply Tags</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Center>
	)
}
